import { siteConfig } from "@acme/config";
import { baseLocale, getLocale, locales } from "~/paraglide/runtime.js";
import { getBaseUrl } from "./utils";

const ogLocaleMap: Record<string, string> = {
  en: "en_US",
  lv: "lv_LV",
};

export function buildLocalizedHref(
  baseUrl: string,
  locale: string,
  path: string
) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (locale === baseLocale) {
    return `${baseUrl}${normalizedPath}`;
  }

  return `${baseUrl}/${locale}${normalizedPath}`;
}

export function seo({
  title,
  description,
  keywords,
  image = `${getBaseUrl()}/images/cover.png`,
  imageAlt,
  author,
  type = "website",
  path,
  localePaths,
  canonicalLocale,
  noindex = false,
}: {
  title: string;
  description?: string | null;
  image?: string | null;
  /** The cover's alt text; falls back to the title for og:image:alt. */
  imageAlt?: string | null;
  keywords?: string | null;
  author?: string | null;
  type?: "website" | "article" | "video" | "book" | "profile";
  path: string;
  localePaths?: Record<string, string>;
  canonicalLocale?: string;
  /**
   * Keep the page out of search results. Emits `noindex, follow` — the page
   * stays crawlable on purpose, because a crawler that is blocked in
   * robots.txt never reads this tag and can still index the URL from an
   * inbound link. Pages set this must also stay out of sitemap.xml, or Search
   * Console reports them as "Submitted URL marked noindex".
   */
  noindex?: boolean;
}) {
  const baseUrl = getBaseUrl();
  const currentLocale = getLocale();
  const localizedHref = (loc: string, p: string) =>
    buildLocalizedHref(baseUrl, loc, p);
  const requestedPageLocale = canonicalLocale ?? currentLocale;
  const requestedCanonicalPath = localePaths?.[requestedPageLocale] ?? path;
  const pageLocale =
    localePaths?.[currentLocale] === requestedCanonicalPath
      ? currentLocale
      : requestedPageLocale;
  const canonicalPath = localePaths?.[pageLocale] ?? path;
  const localizedUrl = localizedHref(pageLocale, canonicalPath);
  const imageAltText = imageAlt || `${title} - ${siteConfig.title}`;

  const tags = [
    { title },
    ...(description ? [{ content: description, name: "description" }] : []),
    ...(keywords ? [{ content: keywords, name: "keywords" }] : []),
    { content: author ?? siteConfig.author.name, name: "author" },
    {
      content: noindex ? "noindex, follow" : "index, follow",
      name: "robots",
    },
    { content: title, name: "twitter:title" },
    ...(description
      ? [{ content: description, name: "twitter:description" }]
      : []),
    { content: siteConfig.author.handle, name: "twitter:creator" },
    { content: siteConfig.author.handle, name: "twitter:site" },
    { content: "on", name: "twitter:widgets:new-embed-design" },
    { content: localizedUrl, name: "twitter:url" },
    { content: type, property: "og:type" },
    { content: siteConfig.title, property: "og:site_name" },
    { content: title, property: "og:title" },
    ...(description
      ? [{ content: description, property: "og:description" }]
      : []),
    {
      content: ogLocaleMap[pageLocale] ?? "en_US",
      property: "og:locale",
    },
    ...locales
      .filter((loc) => loc !== pageLocale)
      .map((loc) => ({
        content: ogLocaleMap[loc] ?? "en_US",
        property: "og:locale:alternate",
      })),
    { content: localizedUrl, property: "og:url" },
    ...(image
      ? [
          { content: image, name: "twitter:image" },
          { content: imageAltText, name: "twitter:image:alt" },
          { content: "summary_large_image", name: "twitter:card" },
          { content: image, property: "og:image" },
          { content: imageAltText, property: "og:image:alt" },
          { content: "image/png", property: "og:image:type" },
          { content: "1200", property: "og:image:width" },
          { content: "630", property: "og:image:height" },
        ]
      : []),
  ];

  const links = [
    { href: localizedUrl, rel: "canonical" },
    ...locales.map((loc) => ({
      href: localizedHref(loc, localePaths?.[loc] ?? path),
      hrefLang: loc,
      rel: "alternate",
    })),
    {
      href: localizedHref(baseLocale, localePaths?.[baseLocale] ?? path),
      hrefLang: "x-default",
      rel: "alternate",
    },
  ];

  return { links, meta: tags };
}
